<template>
  <div class="auth-register">
    <AuthViewIntro
      :title="$t('profile.create-your-account')"
      :subtitle="$t('profile.auth.register-sub-heading')" />

    <AuthAlert v-if="errorMessage" :message="errorMessage" />

    <p class="auth-register__field-label">{{ $t('profile.auth.title') }}</p>
    <div class="auth-register__pills">
      <button
        v-for="option in genders"
        :key="option.value"
        type="button"
        class="auth-register__pill"
        :class="{ 'is-active': form.gender === option.value }"
        @click="form.gender = option.value">
        {{ option.label }}
      </button>
    </div>

    <div class="auth-register__row">
      <AuthTextField
        v-model="form.first_name"
        required
        autocomplete="given-name"
        :label="$t('ticket-cart.first-name')"
        placeholder="John"
        :error="firstNameError"
        @blur="touched.first_name = true" />
      <AuthTextField
        v-model="form.last_name"
        autocomplete="family-name"
        :label="$t('ticket-cart.last-name')"
        :placeholder="$t('ticket-cart.last-name')" />
    </div>

    <AuthTextField
      v-model="form.email"
      required
      type="email"
      autocomplete="email"
      :label="$t('ticket-cart.email')"
      placeholder="user@guestaps.com"
      :hint="$t('profile.register-sub')"
      :error="emailError"
      @blur="touched.email = true" />

    <AuthPhoneField
      required
      :label="$t('general.mobile-number')"
      :dial-code="form.dial_code"
      :number="form.phone_number"
      @update:dialCode="form.dial_code = $event"
      @update:number="form.phone_number = $event" />

    <AuthTextField
      v-model="form.address"
      required
      type="textarea"
      autocomplete="street-address"
      :label="$t('general.address')" />

    <AuthSubmitButton
      :disabled="!isFormValid"
      :loading="isSubmitting"
      @click="submit">
      {{ $t('profile.register') }}
    </AuthSubmitButton>

    <p class="auth-register__footer">
      {{ $t('profile.already-registered') }},
      <button type="button" class="auth-register__link" @click="$emit('switch-view', 'login')">
        {{ $t('profile.login-here') }}
      </button>
    </p>
  </div>
</template>

<script>
  const { email, required } = require('vuelidate/lib/validators');
  import country from '@/data/country';
  import AuthViewIntro from '../AuthViewIntro.vue';
  import AuthTextField from '../AuthTextField.vue';
  import AuthPhoneField from '../AuthPhoneField.vue';
  import AuthSubmitButton from '../AuthSubmitButton.vue';
  import AuthAlert from '../AuthAlert.vue';
  import { registerRequest } from '@/utils/authApi.js';
  import { applyRegisterSuccess } from '@/utils/authSession.js';

  const DEFAULT_COUNTRY_CODE = 'ID';

  export default {
    name: 'RegisterView',
    components: {
      AuthViewIntro,
      AuthTextField,
      AuthPhoneField,
      AuthSubmitButton,
      AuthAlert
    },
    data() {
      return {
        form: {
          gender: 'Mr',
          first_name: '',
          last_name: '',
          email: '',
          phone_number: '',
          dial_code: {},
          address: ''
        },
        touched: {
          first_name: false,
          email: false
        },
        errorMessage: '',
        isSubmitting: false
      }
    },
    validations: {
      form: {
        email: { required, email },
        first_name: { required }
      }
    },
    computed: {
      genders() {
        return [
          { value: 'Mr', label: this.$t('profile.auth.mr') },
          { value: 'Mrs', label: this.$t('profile.auth.mrs') }
        ];
      },
      isFormValid() {
        return !this.$v.form.email.$invalid
          && !this.$v.form.first_name.$invalid
          && this.form.address !== ''
          && this.form.phone_number !== '';
      },
      firstNameError() {
        if (!this.touched.first_name) return '';
        return this.$v.form.first_name.required ? '' : this.$t('profile.auth.required-field');
      },
      emailError() {
        if (!this.touched.email) return '';
        if (!this.$v.form.email.required) return this.$t('profile.auth.required-field');
        if (!this.$v.form.email.email) return this.$t('profile.auth.invalid-email');
        return '';
      }
    },
    created() {
      this.form.dial_code = country.find(item => item.code === DEFAULT_COUNTRY_CODE);
    },
    methods: {
      submit() {
        if (!this.isFormValid || this.isSubmitting) return;

        this.isSubmitting = true;
        this.errorMessage = '';

        registerRequest(this.form).then(result => {
          if (!result.isSuccess) {
            this.errorMessage = result.message || this.$t('profile.auth.generic-error');
            this.isSubmitting = false;
            return;
          }
          return applyRegisterSuccess(result.data, result.payload).then(() => {
            this.isSubmitting = false;
            this.$emit('success');
          });
        }).catch(() => {
          this.errorMessage = this.$t('profile.auth.generic-error');
          this.isSubmitting = false;
        });
      }
    }
  }
</script>

<style scoped>
.auth-register__field-label {
  margin: 0 0 6px;
  font-size: 0.8125rem;
  font-weight: 500;
  color: #1C2B36;
}
.auth-register__pills {
  display: flex;
  gap: 8px;
  margin-bottom: 16px;
}
.auth-register__pill {
  flex: 1;
  padding: 10px 12px;
  font-size: 0.875rem;
  color: #1C2B36;
  background: #fff;
  border: 1px solid #E0E8EF;
  border-radius: 12px;
  cursor: pointer;
}
.auth-register__pill.is-active {
  font-weight: 600;
  color: var(--merchant-primary-color, #1AA179);
  background: var(--merchant-primary-color-alpha-15, rgba(26, 161, 121, 0.1));
  border-color: var(--merchant-primary-color, #1AA179);
}
.auth-register__row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}
.auth-register__footer {
  margin: 16px 0 0;
  font-size: 0.8125rem;
  color: #6B7886;
  text-align: center;
}
.auth-register__link {
  padding: 0;
  font-size: 0.8125rem;
  font-weight: 600;
  color: var(--merchant-primary-color, #1AA179);
  background: transparent;
  border: none;
  cursor: pointer;
}
</style>



// WEBPACK FOOTER //
// src/components/v2/auth/views/RegisterView.vue